import { Flash } from '@carbon/icons-react';
import * as React from 'react';
import { PortalLogo } from 'vet-bones/design-system/components';

import {
  SduDashboardConnection,
  SduEndpointTableRow,
  aggregateByDate,
  aggregateByEndpoint,
  aggregateByGroup,
  formatDateForApi,
  formatTotalUnits,
} from 'src/ui/components/sdu-dashboard/aggregations';
import { SduConsumptionBreakdown } from 'src/ui/components/sdu-dashboard/SduConsumptionBreakdown';
import { SduConsumptionChart } from 'src/ui/components/sdu-dashboard/SduConsumptionChart';
import { SduDateRangePicker } from 'src/ui/components/sdu-dashboard/SduDateRangePicker';
import { SduEndpointConsumptionTable } from 'src/ui/components/sdu-dashboard/SduEndpointConsumptionTable';
import {
  SDU_DASHBOARD_TILE_ENDPOINT_COUNT,
  SDU_DASHBOARD_TILE_TOTAL,
} from 'src/ui/constants/testIds';
import { OrganizationDashboardCard } from 'src/ui/containers/organization/components/OrganizationDashboardCard';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  TimeGrain,
  useGetTotalUnitUsageQuery,
  useGetUnitUsageQuery,
} from 'src/ui/graph/types';
import * as copy from 'src/ui/templates/copy';

interface SduDashboardBodyProps {
  connectionId?: string;
  connections?: SduDashboardConnection[];
  onEndpointClick?: (row: SduEndpointTableRow) => void;
  orgDomain?: string;
}

export const SduDashboardBody: React.VFC<SduDashboardBodyProps> = ({
  connectionId,
  connections,
  onEndpointClick,
  orgDomain,
}) => {
  const isOverview = !connectionId;

  const [dateRange, setDateRange] = React.useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [timeGrain, setTimeGrain] = React.useState(TimeGrain.Day);

  const [startDate, endDate] = dateRange;
  const startDateStr = formatDateForApi(startDate);
  const endDateStr = formatDateForApi(endDate);

  const totalQuery = useGetTotalUnitUsageQuery(graphQLClient, {
    connectionId,
    org_domain: orgDomain,
  });

  const unitUsageQuery = useGetUnitUsageQuery(graphQLClient, {
    connectionId,
    endDate: endDateStr,
    org_domain: orgDomain,
    startDate: startDateStr,
    timeGrain,
  });

  const records = unitUsageQuery.data?.getUnitUsage;

  const chartData = React.useMemo(
    () => aggregateByDate(records ?? [], timeGrain),
    [records, timeGrain]
  );
  const breakdown = React.useMemo(() => aggregateByGroup(records ?? []), [
    records,
  ]);
  const tableRows = React.useMemo(
    () => aggregateByEndpoint(records ?? [], connections ?? []),
    [connections, records]
  );

  const totalUnitsRaw =
    totalQuery.data?.getTotalUnitUsage?.totalUnitsConsumed ?? 0;
  const totalValue = formatTotalUnits(totalUnitsRaw);

  const chartTitle = isOverview
    ? copy.components.sduDashboard.chart.titleOverview
    : copy.components.sduDashboard.chart.titleEndpoint;

  return (
    <>
      <div className="organization-dashboard-cards organization-dashboard-cards--fill">
        <OrganizationDashboardCard
          icon={PortalLogo}
          testId={SDU_DASHBOARD_TILE_TOTAL}
          title={copy.components.sduDashboard.totalTileLabel}
          value={totalValue}
        />
        {isOverview ? (
          <OrganizationDashboardCard
            icon={Flash}
            testId={SDU_DASHBOARD_TILE_ENDPOINT_COUNT}
            title={copy.components.sduDashboard.endpointsTileLabel}
            value={String(tableRows.length)}
          />
        ) : null}
      </div>
      <SduDateRangePicker onChange={setDateRange} value={dateRange} />
      <SduConsumptionChart
        data={chartData}
        isLoading={unitUsageQuery.isLoading}
        onTimeGrainChange={setTimeGrain}
        timeGrain={timeGrain}
        title={chartTitle}
      />
      <SduConsumptionBreakdown
        breakdown={breakdown}
        isLoading={unitUsageQuery.isLoading}
      />
      {isOverview ? (
        <SduEndpointConsumptionTable
          onRowClick={onEndpointClick}
          rows={tableRows}
        />
      ) : null}
    </>
  );
};
